import { useCallback, useEffect, useMemo, useState } from "react";
import { dailyChallengeService } from "../../services/dailyChallenge.service";
import { DailyChallengeWithScores } from "../../interfaces/dailyChallenge.interface";
import { Game } from "../../interfaces/game.interface";
import { gameStore } from "../../gStore/game.store";
import { useGameNavigator } from "../../hooks/useGameNavigator";
import { formatScore } from "../../utilities/formatter";
import { Wheel } from "react-custom-roulette";
import { WheelData } from "react-custom-roulette/dist/components/Wheel/types";
import { shuffle } from "lodash";

const DAILY_CHALLENGE_ID_KEY = "last-daily-challenge-id";

export const DailyChallengeSection = () => {
  const [dailyChallenge, setDailyChallenge] =
    useState<DailyChallengeWithScores | null>(null);

  const [game, setGame] = useState<Game | null>(null);
  const [hasSpun, setHasSpun] = useState(false);
  const [isSpinning, setIsSpinning] = useState(false);

  const { navigateToGame } = useGameNavigator(game);

  useEffect(() => {
    dailyChallengeService.getDailyChallenge().then((dailyChallenge) => {
      setDailyChallenge(dailyChallenge);

      // Check if user has already spun today using either score or localStorage
      if (
        dailyChallenge.scores.user !== null ||
        localStorage.getItem(DAILY_CHALLENGE_ID_KEY) ===
        dailyChallenge.id.toString()
      ) {
        setHasSpun(true);
      }

      // Fetch game details from store
      const game = gameStore.data.games.find(
        (game) => game.id === dailyChallenge.game_id
      );
      if (game) {
        setGame(game);
      } else {
        console.error("Game not found for daily challenge");
      }
    });
  }, []);

  const handleStartSpin = useCallback(() => {
    if (!dailyChallenge) return;

    // Mark that user has spun today
    localStorage.setItem(DAILY_CHALLENGE_ID_KEY, dailyChallenge.id.toString());

    setIsSpinning(true);
  }, [dailyChallenge]);

  const handleSpinComplete = useCallback(() => {
    // Wait a second, then switch to the normal view
    setTimeout(() => {
      setHasSpun(true);
    }, 1000);
  }, []);

  const [wheelData, prizeNumber] = useMemo(() => {
    if (!dailyChallenge || !game || hasSpun) return [[], 0];

    // Start with the selected game
    const wheelData: WheelData[] = [{ option: game.id }];

    // Get other games
    const otherGames = gameStore.data.games.filter(
      (g) => g.leaderboard_support && g.id !== dailyChallenge.game_id
    );

    // Shuffle other games and take 9
    const shuffledGames = shuffle(otherGames).slice(0, 9);

    // Add shuffled games to the wheel data
    shuffledGames.forEach((g) => {
      wheelData.push({ option: g.id });
    });

    // Shuffle options to randomize position of the selected game
    const shuffledWheelData = shuffle(wheelData);

    // Find the index of the selected game
    const prizeNumber = shuffledWheelData.findIndex(
      (data) => data.option === game.id
    );

    return [shuffledWheelData, prizeNumber];
  }, [dailyChallenge, game, hasSpun]);

  if (dailyChallenge === null || game === null) return null;

  let didHighlight = false;

  return (
    <div className="py-8">
      <div className="container flex justify-center">
        {hasSpun ? (
          <div className="flex gap-4 p-4 rounded-lg bg-dark2">
            {/* Left */}
            <div className="flex flex-col gap-2">
              <p className="text-center font-bold text-2xl uppercase">
                Daily Challenge
              </p>

              <div
                className="flex flex-col gap-2 bg-dark2 p-2 rounded-lg cursor-pointer"
                onClick={() => navigateToGame()}
              >
                <img
                  src={game.thumbnail}
                  className="w-72 rounded-lg cursor-pointer"
                  alt={game.id}
                />
                <p className="text-center font-bold text-lg uppercase">
                  {game.id}
                </p>
              </div>
            </div>

            {/* Divider */}
            <div className="border-l border-gray-700" />

            {/* Right */}
            <div className="flex flex-col gap-2 min-w-48">
              <div>
                <p className="font-bold text-sm text-center uppercase">
                  Your Score
                </p>
                <p className="font-bold text-4xl text-center">
                  {dailyChallenge.scores.user
                    ? formatScore(dailyChallenge.scores.user)
                    : "-"}
                </p>
              </div>

              <div className="border-b border-gray-700" />

              <div>
                <p className="font-bold text-sm text-center uppercase">
                  Top Scores
                </p>

                <div className="flex flex-col h-40 px-4 overflow-y-auto">
                  {dailyChallenge.scores.top.map((score, index) => {
                    // Only highlight the first instance of the user's score
                    const isHighlighted =
                      score === dailyChallenge.scores.user && !didHighlight;
                    if (isHighlighted) didHighlight = true;

                    return (
                      <div
                        key={index}
                        className={`flex gap-32 items-center justify-between ${
                          isHighlighted ? "text-wowsee" : ""
                        }`}
                      >
                        <p
                          className={`text-sm ${
                            isHighlighted ? "" : "text-gray-400"
                          }`}
                        >
                          {index + 1}
                        </p>
                        <p key={index} className="font-light text-2xl">
                          {formatScore(score)}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4 items-center">
            <p className="text-center font-bold text-2xl uppercase">
              Daily Challenge
            </p>
            <div className="daily-wheel" onClick={handleStartSpin}>
              <Wheel
                mustStartSpinning={isSpinning}
                prizeNumber={prizeNumber}
                data={wheelData}
                backgroundColors={["#86EFAC", "#3C6E71"]}
                onStopSpinning={handleSpinComplete}
                fontSize={16}
                spinDuration={0.7}
              />
            </div>
            <button
              className="cursor-pointer font-bold text-[20px] bg-green-300 hover:bg-green-400 text-black rounded-md px-6 py-2 w-fit text-center"
              onClick={handleStartSpin}
            >
              Spin Now
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
